import React, { JSX, Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import Layout from './components/layout/Layout';
import {
  useAuth,
  ProtectedRoute,
  Login,
  Logout,
  LoginCallback,
  LogoutCallback,
} from './auth';

// Lazy load pages for better performance
const LandingPage = lazy(() => import('./pages/landing-page/LandingPage.tsx'));
const ForenOverview = lazy(() => import('./pages/foren-overview/ForenOverview.tsx'));
const CreateForum = lazy(() => import('./pages/create-forum/CreateForum.tsx'));
const EditForum = lazy(() => import('./pages/edit-forum/EditForum.tsx'));
const ViewForum = lazy(() => import('./pages/view-forum/ViewForum.tsx'));
const PreviewForum = lazy(() => import('./pages/preview-forum/PreviewForum.tsx'));
const ManageForen = lazy(() => import('./pages/manage-foren/ManageForen.tsx'));
const AdminConsole = lazy(() => import('./pages/admin-console/AdminConsole.tsx'));
const AdminConsoleEditUser = lazy(() => import('./pages/admin-console/AdminConsoleEditUser.tsx'));
const AdminConsoleAddUser = lazy(() => import('./pages/admin-console/AdminConsoleAddUser.tsx'));
const NotFound = lazy(() => import('./pages/not-found/NotFound.tsx'));

const LoadingFallback: React.FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
    <CircularProgress />
  </Box>
);

const App: React.FC = () => {
  const { isLoading, roles } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  // Determine default route based on authentication status
  const renderDefaultRoute = (): JSX.Element => {
    if (roles.length > 0) {
      return <Route path="/" element={<ForenOverview />} />;
    }
    return <Route path="/" element={<LandingPage />} />;
  };

  return (
    <Layout>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Authentication routes */}
          <Route path="/authentication/login" element={<Login />} />
          <Route path="/authentication/login-callback" element={<LoginCallback />} />
          <Route path="/authentication/logout" element={<Logout />} />
          <Route path="/authentication/logout-callback" element={<LogoutCallback />} />

          {/* Public/conditional routes */}
          {renderDefaultRoute()}

          {/* Protected routes */}
          <Route path="/foren" element={<ProtectedRoute><ForenOverview /></ProtectedRoute>} />
          <Route path="/foren/verwalten" element={<ProtectedRoute><ManageForen /></ProtectedRoute>} />
          <Route path="/foren/adminkonsole" element={<ProtectedRoute><AdminConsole /></ProtectedRoute>} />
          <Route path="/foren/view/:forumid" element={<ProtectedRoute><ViewForum /></ProtectedRoute>} />
          <Route path="/foren/preview/:forumid" element={<ProtectedRoute><PreviewForum /></ProtectedRoute>} />
          <Route path="/foren/verwalten/:forumid" element={<ProtectedRoute><EditForum /></ProtectedRoute>} />
          <Route path="/foren/adminkonsole/edituser/:usercommonid" element={<ProtectedRoute><AdminConsoleEditUser /></ProtectedRoute>} />
          <Route path="/foren/adminkonsole/adduser" element={<ProtectedRoute><AdminConsoleAddUser /></ProtectedRoute>} />
          <Route path="/foren/erstellen" element={<ProtectedRoute><CreateForum /></ProtectedRoute>} />

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  );
};

export default App;
